import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import './ActivityLogs.css';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const fetchLogs = useCallback(async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.category = filterType;
      if (filterLevel !== 'all') params.level = filterLevel;
      const res = await adminService.getLogs(params);
      const rawLogs = res?.logs ?? [];
      setLogs(rawLogs.map(l => ({
        id: l.id,
        timestamp: l.created_at,
        level: l.level,
        type: l.category,
        message: l.message,
        details: l.metadata ? (typeof l.metadata === 'string' ? l.metadata : JSON.stringify(l.metadata)) : '',
        user: l.user_email || 'System'
      })));
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterLevel]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Filtrar logs
  const filteredLogs = logs.filter(log => {
    const matchesType = filterType === 'all' || log.type === filterType;
    const matchesLevel = filterLevel === 'all' || log.level === filterLevel;
    const matchesSearch = (log.message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (log.details || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (log.user || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesType && matchesLevel && matchesSearch;
  });

  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Obtener nombre del tipo
  const getTypeName = (type) => {
    const typeNames = {
      'system': 'Sistema',
      'auth': 'Autenticación',
      'product': 'Producto',
      'user': 'Usuario',
      'backup': 'Backup'
    };
    return typeNames[type] || type;
  };

  // Obtener nombre del nivel
  const getLevelName = (level) => {
    const levelNames = {
      'info': 'Información',
      'success': 'Éxito',
      'warning': 'Advertencia',
      'error': 'Error',
      'critical': 'Crítico'
    };
    return levelNames[level] || level;
  };

  // Obtener estadísticas
  const stats = {
    total: logs.length,
    byLevel: logs.reduce((acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1;
      return acc;
    }, {}),
    byType: logs.reduce((acc, log) => {
      acc[log.type] = (acc[log.type] || 0) + 1;
      return acc;
    }, {}),
    recent: logs.filter(log => {
      const logDate = new Date(log.timestamp);
      const oneHourAgo = new Date();
      oneHourAgo.setHours(oneHourAgo.getHours() - 1);
      return logDate > oneHourAgo;
    }).length
  };

  if (loading) {
    return (
      <div className="activity-logs">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Cargando logs de actividad...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-logs">
      <div className="container">
        <div className="section-header">
          <h2>📊 Logs de Actividad</h2>
          <p>Monitorea la actividad del sistema en tiempo real</p>
        </div>

        {/* Estadísticas */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-list"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total de Logs</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.recent}</h3>
              <p>Última Hora</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.byLevel.error || 0}</h3>
              <p>Errores</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div className="stat-content">
              <h3>{Object.keys(stats.byType).length}</h3>
              <p>Categorías</p>
            </div>
          </div>
        </div>

        {/* Controles */}
        <div className="logs-controls">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar en logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-controls">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Todos los tipos</option>
              <option value="system">Sistema</option>
              <option value="auth">Autenticación</option>
              <option value="product">Producto</option>
              <option value="user">Usuario</option>
              <option value="backup">Backup</option>
            </select>
            
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
            >
              <option value="all">Todos los niveles</option>
              <option value="info">Información</option>
              <option value="success">Éxito</option>
              <option value="warning">Advertencia</option>
              <option value="error">Error</option>
              <option value="critical">Crítico</option>
            </select>
          </div>
        </div>

        {/* Lista de logs */}
        <div className="logs-container">
          {filteredLogs.length === 0 ? (
            <div className="no-logs">
              <i className="fas fa-inbox"></i>
              <h3>No hay logs para mostrar</h3>
              <p>Los logs aparecerán aquí cuando haya actividad en el sistema.</p>
            </div>
          ) : (
            <div className="logs-list">
              {filteredLogs.map((log) => (
                <div key={log.id} className={`log-item ${log.level}`}>
                  <div className="log-header">
                    <div className="log-level">
                      <span className={`level-badge ${log.level}`}>
                        {getLevelName(log.level)}
                      </span>
                    </div>
                    <div className="log-timestamp">
                      {formatDate(log.timestamp)}
                    </div>
                  </div>
                  
                  <div className="log-content">
                    <div className="log-type">
                      <span className="type-badge">
                        {getTypeName(log.type)}
                      </span>
                    </div>
                    
                    <div className="log-message">
                      <h4>{log.message}</h4>
                      <p>{log.details}</p>
                    </div>
                    
                    <div className="log-meta">
                      <span className="log-user">
                        <i className="fas fa-user"></i>
                        {log.user}
                      </span>
                      <span className="log-ip">
                        <i className="fas fa-network-wired"></i>
                        {log.ip}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Información de filtros */}
        <div className="filter-info">
          <p>
            Mostrando {filteredLogs.length} de {logs.length} logs
            {filterType !== 'all' && ` del tipo ${getTypeName(filterType)}`}
            {filterLevel !== 'all' && ` del nivel ${getLevelName(filterLevel)}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
